import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../firebase';
import { getUserProfileCached } from './userProfileCache';

// P0-CLI-1: All friend mutations now go through Cloud Functions for atomicity

export async function sendFriendRequest(recipientId, meta = {}) {
  const me = auth.currentUser;
  if (!me || !recipientId || me.uid === recipientId) return;
  const callable = httpsCallable(functions, 'sendFriendRequest');
  await callable({
    recipientId,
    displayName: meta.displayName || me.displayName || me.uid,
    photoURL: meta.photoURL || me.photoURL || ''
  });
}

export async function cancelFriendRequest(recipientId) {
  const me = auth.currentUser;
  if (!me || !recipientId) return;
  const callable = httpsCallable(functions, 'cancelFriendRequest');
  await callable({ recipientId });
}

export async function acceptFriendRequest(requesterId) {
  const me = auth.currentUser;
  if (!me || !requesterId) return;
  const callable = httpsCallable(functions, 'acceptFriendRequest');
  const resp = await callable({ requesterId });
  const data = resp && resp.data ? resp.data : {};
  const reward = typeof window !== 'undefined' ? window.__REWARD__ : null;
  if (reward && typeof reward.showCombined === 'function') {
    const badge = Array.isArray(data.badgeAwards) && data.badgeAwards.length > 0 ? data.badgeAwards[0] : null;
    reward.showCombined({
      title: 'Friend added!',
      xp: Number(data.awardedXp || 0),
      badge: badge ? { name: badge.name, level: badge.level, iconURL: badge.iconURL } : null,
      lines: ['Say hi and go ducking together!'],
      ctas: [{ label: 'View Friends', href: '/friends' }]
    });
  }
}

export async function declineFriendRequest(requesterId) {
  const me = auth.currentUser;
  if (!me || !requesterId) return;
  const callable = httpsCallable(functions, 'declineFriendRequest');
  await callable({ requesterId });
}

// P1-DATA-1: removeFriend now uses Cloud Function to delete both sides atomically
export async function removeFriend(friendId) {
  const me = auth.currentUser;
  if (!me || !friendId) return;
  const callable = httpsCallable(functions, 'removeFriend');
  await callable({ friendId });
}

export function subscribeFriends(uid, cb) {
  const q = collection(db, 'users', uid, 'friends');
  return onSnapshot(q, (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

export function subscribeRequests(uid, cb, onError) {
  const q = query(collection(db, 'users', uid, 'friendRequests'), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (err) => {
      try { (onError || console.error)('subscribeRequests snapshot error', err); }
      catch (handlerErr) { console.error('subscribeRequests error handler failed', handlerErr); }
    }
  );
} 

// Subscribe to friend requests and return an enriched list suitable for UI display.
// Each entry is normalized to: { id: requesterUid, displayName, photoURL }
export function subscribeEnrichedRequests(uid, cb, onError) {
  let cancelled = false;

  const unsub = subscribeRequests(uid, async (raw) => {
    try {
      const enriched = await Promise.all((raw || []).map(async (req) => {
        const requesterId = (req && (req.fromUserId || req.id)) || '';
        const fromDisplayName = req?.fromDisplayName || '';
        const fromPhotoURL = req?.fromPhotoURL || '';

        if (!requesterId) return { id: '' };

        // If request doc already carries display data, prefer it.
        if (fromDisplayName || fromPhotoURL) {
          return { id: requesterId, displayName: fromDisplayName, photoURL: fromPhotoURL };
        }

        // Otherwise, fallback to cached merged profile.
        const data = await getUserProfileCached(requesterId);
        return { id: requesterId, displayName: data.displayName || '', photoURL: data.photoURL || '' };
      }));

      if (!cancelled) cb(enriched.filter(r => r && r.id));
    } catch (err) {
      if (!cancelled) {
        try { (onError || console.error)('subscribeEnrichedRequests failed', err); }
        catch (handlerErr) { console.error('subscribeEnrichedRequests error handler failed', handlerErr); }
      }
    }
  }, (label, err) => {
    if (!cancelled) {
      try { (onError || console.error)(label, err); }
      catch (handlerErr) { console.error('subscribeEnrichedRequests error handler failed', handlerErr); }
    }
  });

  return () => {
    cancelled = true;
    try { unsub(); } catch (err) { console.error('subscribeEnrichedRequests cleanup failed', err); }
  };
}
